/**
 * ATL-1313 — Postgres's own text rendering of a `timestamptz`, rewritten as strict ISO-8601.
 *
 * WHY THIS LIVES IN THE API CLIENT
 *
 * `jsonb_build_object('startDate', v_supply_date::TEXT, …)` renders a `timestamptz` in POSTGRES's
 * output format — a space separator and an offset truncated to `+00`. That is not an ECMAScript
 * Date Time String, so parsing it is left to each engine's non-standard fallback, and the two
 * engines this codebase runs on disagree. Measured against Hermes 0.13 and node, not read off the
 * spec:
 *
 *   "2026-09-09 23:29:55.534+00"      space, two-digit offset  -> Hermes Invalid Date · V8 parses
 *   "2026-09-09T23:29:55.534+00"      T,     two-digit offset  -> Hermes Invalid Date · V8 parses
 *   "2026-09-09 23:29:55.534+00:00"   space, full offset       -> both parse
 *
 * The element Hermes rejects is the TRUNCATED OFFSET, not the separator. `date-fns@2` answers an
 * invalid date by throwing `RangeError: Invalid time value`, and the payments list formats such a
 * value inside JSX, where React makes the throw fatal — a crash loop no browser can reproduce.
 *
 * It lives HERE, rather than in the app, because `deserializeDates` is the first thing that sees
 * one of these strings and the last thing that still HAS it: `toTimestampLike` used to wrap it into
 * a `TimestampLike` closing over an Invalid Date and throw the string away. Normalising before that
 * point is what lets every consumer keep working. `apps/atlas/utils/nestedDateValue.ts` imports
 * this same function rather than keeping a second copy, so the two layers cannot drift.
 */

/**
 * Postgres's text rendering: `YYYY-MM-DD[ T]HH:MM:SS[.ffffff]±HH[:MM]`.
 *
 * Deliberately anchored and deliberately narrow: it must not match free text that merely contains
 * digits, because it is reached with arbitrary snapshot values.
 *
 * A string that ALREADY carries `±HH:MM` matches too and rewrites to itself — idempotence instead
 * of an exclusion clause that would have to stay in step with the pattern. A `Z`-terminated ISO
 * string carries no `[+-]` and never matches at all.
 */
const POSTGRES_TIMESTAMPTZ =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?([+-]\d{2})(?::(\d{2}))?$/;

/**
 * Rewrite Postgres's text rendering of a `timestamptz` as strict ISO-8601, or `null` when the
 * string is not one.
 *
 * Only the SEPARATOR, the OFFSET WIDTH and the FRACTION WIDTH change; no field is reinterpreted, so
 * the instant is carried through exactly, to the millisecond a `Date` can hold.
 *
 * The fraction is normalised to EXACTLY three digits, the only width the ECMAScript grammar
 * defines. Postgres writes microseconds whenever the instant came from `NOW()` (`.629316`) and
 * trims trailing zeros when it did not (`.5`); both are outside the grammar, so both would be left
 * to the same engine-specific fallback that produced this ticket.
 *
 * An offset carrying SECONDS (`+00:53:28`, which Postgres emits for a pre-standard-time LMT zone)
 * is deliberately not matched: truncating it would move the instant.
 */
export function postgresTimestampToIso(value: string): string | null {
  const m = POSTGRES_TIMESTAMPTZ.exec(value);
  if (!m) return null;
  const [, date, time, fraction, offsetHours, offsetMinutes] = m;
  const milliseconds = fraction === undefined ? '' : `.${fraction.slice(0, 3).padEnd(3, '0')}`;
  return `${date}T${time}${milliseconds}${offsetHours}:${offsetMinutes ?? '00'}`;
}
